import React, { useState } from 'react';
import { motion, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { MapPin, Briefcase, CheckCircle2, Info } from 'lucide-react';
import { User } from '../types';
interface ProfileCardProps {
  user: User;
  onSwipe: (direction: 'left' | 'right') => void;
  onDetailClick?: () => void;
  style?: React.CSSProperties;
  drag?: boolean;
}
export function ProfileCard({
  user,
  onSwipe,
  onDetailClick,
  style,
  drag = true
}: ProfileCardProps) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]);
  // Visual indicators for swipe
  const likeOpacity = useTransform(x, [50, 150], [0, 1]);
  const nopeOpacity = useTransform(x, [-50, -150], [0, 1]);
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.x > 100) {
      onSwipe('right');
    } else if (info.offset.x < -100) {
      onSwipe('left');
    }
  };
  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentPhotoIndex < user.photos.length - 1) {
      setCurrentPhotoIndex(prev => prev + 1);
    }
  };
  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(prev => prev - 1);
    }
  };
  return <motion.div style={{
    x,
    rotate,
    opacity,
    ...style
  }} drag={drag ? 'x' : false} dragConstraints={{
    left: 0,
    right: 0
  }} onDragEnd={handleDragEnd} className="absolute inset-0 w-full h-full rounded-3xl overflow-hidden shadow-xl bg-white cursor-grab active:cursor-grabbing" whileTap={{
    scale: 1.02
  }} data-id="element-269">
      {/* Background Image */}
      <div className="absolute inset-0 bg-zinc-200" data-id="element-270">
        <img src={user.photos[currentPhotoIndex]} alt={user.name} className="w-full h-full object-cover transition-opacity duration-300" draggable={false} data-id="element-271" />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/80" data-id="element-272" />

        {/* Photo Navigation Zones */}
        <div className="absolute inset-0 flex" data-id="element-273">
          <div className="w-1/2 h-full z-10" onClick={handlePrevPhoto} data-id="element-274" />
          <div className="w-1/2 h-full z-10" onClick={handleNextPhoto} data-id="element-275" />
        </div>
      </div>

      {/* Photo Indicators */}
      <div className="absolute top-3 left-3 right-3 flex gap-1 z-20" data-id="element-276">
        {user.photos.map((_, idx) => <div key={idx} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${idx === currentPhotoIndex ? 'bg-white' : 'bg-white/30'}`} data-id="element-277" />)}
      </div>

      {/* Swipe Indicators */}
      <motion.div style={{
      opacity: likeOpacity
    }} className="absolute top-12 left-8 border-4 border-green-500 rounded-lg px-4 py-2 -rotate-12 z-20 pointer-events-none" data-id="element-278">
        <span className="text-green-500 font-bold text-4xl tracking-widest uppercase" data-id="element-279">
          Like
        </span>
      </motion.div>

      <motion.div style={{
      opacity: nopeOpacity
    }} className="absolute top-12 right-8 border-4 border-red-500 rounded-lg px-4 py-2 rotate-12 z-20 pointer-events-none" data-id="element-280">
        <span className="text-red-500 font-bold text-4xl tracking-widest uppercase" data-id="element-281">
          Nope
        </span>
      </motion.div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-20 pointer-events-none" data-id="element-282">
        <div className="flex items-end justify-between mb-2" data-id="element-283">
          <div className="flex items-center gap-2" data-id="element-284">
            <h2 className="text-3xl font-bold flex items-baseline gap-2" data-id="element-285">
              {user.name}
              <span className="text-2xl font-normal opacity-90" data-id="element-286">
                {user.age}
              </span>
            </h2>
            {user.verified && <CheckCircle2 size={24} className="text-blue-500 fill-blue-50" data-id="element-287" />}
            {user.online && <div className="w-3 h-3 bg-green-500 rounded-full shadow-sm border border-white/20" data-id="element-288" />}
          </div>
          <button onClick={e => {
          e.stopPropagation();
          onDetailClick?.();
        }} className="pointer-events-auto p-2 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-colors" data-id="element-289">
            <Info size={20} className="text-white" data-id="element-290" />
          </button>
        </div>

        <div className="flex items-center gap-4 text-sm font-medium opacity-90 mb-3" data-id="element-291">
          {user.job && <div className="flex items-center gap-1" data-id="element-292">
              <Briefcase size={16} data-id="element-293" />
              <span data-id="element-294">{user.job}</span>
            </div>}
          <div className="flex items-center gap-1" data-id="element-295">
            <MapPin size={16} data-id="element-296" />
            <span data-id="element-297">{user.distance} miles away</span>
          </div>
        </div>

        <p className="text-white/90 line-clamp-2 mb-4 text-sm leading-relaxed" data-id="element-298">
          {user.bio}
        </p>

        <div className="flex flex-wrap gap-2" data-id="element-299">
          {user.interests.slice(0, 3).map(interest => <span key={interest} className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-medium" data-id="element-300">
              {interest}
            </span>)}
          {user.interests.length > 3 && <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-medium" data-id="element-301">
              +{user.interests.length - 3}
            </span>}
        </div>
      </div>
    </motion.div>;
}